import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DocumentRendererProps } from '@/lib/document-renderer-registry';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS } from '@/lib/menu-chrome';
import { CheckCircle2, Eye, GraduationCap, ListChecks, Loader2, MoreHorizontal, Play } from 'lucide-react';
import { testIds } from '@/lib/test-ids';
import {
  parseQuizFromContent,
  resolveQuizMode,
  resolveQuizRole,
  resolveQuizTimeLimitMinutes,
  shuffleQuestions,
  type QuizConfig,
  type QuizQuestion,
} from '@/lib/quiz-parser';
import {
  clearQuizInProgressSession,
  isResumableQuizDraft,
  loadQuizInProgressSession,
  orderQuestionsByIds,
  type QuizInProgressSession,
} from '@/lib/quiz-in-progress-session';
import {
  buildOptionOrdersForQuestions,
  toCanonicalAnswers,
  type OptionOrder,
} from '@/lib/quiz-option-shuffle';
import {
  fetchQuizRunDetail,
  sampleQuizQuestions,
  submitQuizRun,
  type QuizRunDetail,
  type QuizSelection,
} from '@/lib/api/quiz';
import { useQuizRuns } from '@/hooks/use-quiz';
import { QuizActiveMode } from './QuizActiveMode';
import { QuizPreviewMode } from './QuizPreviewMode';
import { QuizResultsMode } from './QuizResultsMode';
import { QuizRunReviewMode } from './QuizRunReviewMode';
import { QuizRunsTab } from './QuizRunsTab';
import { QuizPracticeMode } from './QuizPracticeMode';
import {
  QuizSessionBuilder,
  type SessionBuilderConfig,
} from './QuizSessionBuilder';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type QuizTab = 'preview' | 'quiz' | 'practice' | 'results' | 'runs';

const QUIZ_TABS: QuizTab[] = ['preview', 'quiz', 'practice', 'results', 'runs'];

function parseQuizTab(raw: string | null): QuizTab | null {
  if (!raw) return null;
  return QUIZ_TABS.includes(raw as QuizTab) ? (raw as QuizTab) : null;
}

interface LastResult {
  run_id?: string;
  mode?: 'exam' | 'survey';
  score_pct: number | null;
  domain_scores: Record<string, number>;
  weak_domains: string[];
  passed: boolean | null;
  time_taken_s: number;
  answered_count?: number;
}

export function QuizDocumentRenderer({
  document,
  /** When false (e.g. Home/list preview), keep tabs local - do not write ?tab= into the URL. */
  syncTabToUrl = true,
}: DocumentRendererProps & { syncTabToUrl?: boolean }) {
  const fm = document.frontmatter ?? {};
  const quizConfig = (fm.quiz_config || {}) as QuizConfig;
  const slug = String(fm.slug || '');
  const quizMode = resolveQuizMode(quizConfig);
  const quizRole = resolveQuizRole(quizConfig);
  const isBank = quizRole === 'bank';
  const isSurvey = quizMode === 'survey';
  const passingScore = Number(quizConfig.passing_score) || 70;
  const timeLimitMinutes = resolveQuizTimeLimitMinutes(quizConfig, quizMode);
  const randomize = quizConfig.randomize !== false;
  const shuffleOptions = quizConfig.shuffle_options !== false;
  const excludeMockSlug =
    quizConfig.exclude_mock_slug || 'quiz-dress-rehearsal-100';
  const defaultSessionCount = Number(quizConfig.default_session?.count) || 20;

  const content = document.content || '';
  const documentId = document.id;
  const allQuestions = useMemo(() => parseQuizFromContent(content), [content]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = parseQuizTab(searchParams.get('tab'));
  const [tab, setTabState] = useState<QuizTab>(() =>
    syncTabToUrl ? (tabFromUrl ?? 'quiz') : 'quiz',
  );
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<LastResult | null>(null);
  const [reviewDetail, setReviewDetail] = useState<QuizRunDetail | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  /** Stable attempt order - never reshuffle while this attempt is live. */
  const [attemptQuestions, setAttemptQuestions] = useState<QuizQuestion[] | null>(null);
  const [attemptSession, setAttemptSession] = useState<QuizInProgressSession | null>(null);
  const [optionOrders, setOptionOrders] = useState<Record<string, OptionOrder>>({});
  const [attemptKey, setAttemptKey] = useState(0);
  /** After submit (or hydrated last run), Play stays locked until Play again. */
  const [retakeActive, setRetakeActive] = useState(false);
  /** Draft exists but user has not clicked Start/Resume yet. */
  const [pendingDraft, setPendingDraft] = useState<QuizInProgressSession | null>(null);
  const [draftChecked, setDraftChecked] = useState(false);
  const [activeSelection, setActiveSelection] = useState<QuizSelection | null>(null);
  const [sessionStarting, setSessionStarting] = useState(false);
  const [practiceDeck, setPracticeDeck] = useState<QuizQuestion[] | null>(null);
  const [practiceSelection, setPracticeSelection] = useState<QuizSelection | null>(null);
  const [sessionTimeLimit, setSessionTimeLimit] = useState<number | null>(null);
  /** Exit-session confirm: exam always; practice when answered >= 3. */
  const [exitConfirm, setExitConfirm] = useState<'exam' | 'practice' | null>(null);

  const { runs, bestScore, trend, loading: runsLoading, refetch: refetchRuns } = useQuizRuns(
    slug || null
  );

  // Reset attempt when navigating to a different quiz document
  useEffect(() => {
    setAttemptQuestions(null);
    setAttemptSession(null);
    setOptionOrders({});
    setRetakeActive(false);
    setLastResult(null);
    setPendingDraft(null);
    setDraftChecked(false);
    setActiveSelection(null);
    setPracticeDeck(null);
    setPracticeSelection(null);
    setSessionTimeLimit(null);
  }, [slug, documentId]);

  const playLocked = !!lastResult && !retakeActive;

  const setTab = useCallback(
    (next: QuizTab) => {
      setTabState(next);
      if (!syncTabToUrl) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'quiz') {
            params.delete('tab');
          } else {
            params.set('tab', next);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams, syncTabToUrl]
  );

  useEffect(() => {
    if (syncTabToUrl && tabFromUrl) {
      setTabState(tabFromUrl);
    }
  }, [syncTabToUrl, tabFromUrl]);

  // Hydrate Results from latest run so the tab persists across refresh
  useEffect(() => {
    if (lastResult || runsLoading || runs.length === 0) return;
    const latest = runs[0];
    const surveyRun = latest.score_pct == null || latest.passed == null;
    setLastResult({
      run_id: latest.id,
      mode: surveyRun || isSurvey ? 'survey' : 'exam',
      score_pct: latest.score_pct != null ? Number(latest.score_pct) : null,
      domain_scores: latest.domain_scores || {},
      weak_domains: latest.weak_domains || [],
      passed: latest.passed == null ? null : !!latest.passed,
      time_taken_s: latest.time_taken_s ?? 0,
      answered_count:
        latest.score_pct == null
          ? Object.values(latest.domain_scores || {}).reduce((a, b) => a + Number(b), 0)
          : undefined,
    });
  }, [runs, runsLoading, lastResult, isSurvey]);

  // Detect in-progress draft for Resume CTA (do not auto-start timer)
  useEffect(() => {
    if (!slug || !documentId || allQuestions.length === 0 || draftChecked) return;
    if (runsLoading) return;
    if (runs.length > 0 && !lastResult && !retakeActive) return;
    if (playLocked) {
      setDraftChecked(true);
      return;
    }
    const draft = loadQuizInProgressSession(slug, documentId);
    if (draft) {
      const ordered = orderQuestionsByIds(allQuestions, draft.questionIds);
      if (ordered && isResumableQuizDraft(draft)) {
        setPendingDraft(draft);
      } else {
        clearQuizInProgressSession(slug, documentId);
      }
    }
    setDraftChecked(true);
  }, [
    slug,
    documentId,
    allQuestions,
    draftChecked,
    runsLoading,
    runs.length,
    lastResult,
    retakeActive,
    playLocked,
  ]);

  const beginAttempt = useCallback(
    (opts?: { resume?: boolean }) => {
      if (!slug || !documentId || allQuestions.length === 0) return;

      if (opts?.resume && pendingDraft) {
        const ordered = orderQuestionsByIds(allQuestions, pendingDraft.questionIds);
        if (ordered) {
          setAttemptQuestions(ordered);
          setAttemptSession(pendingDraft);
          setOptionOrders(pendingDraft.optionOrders || buildOptionOrdersForQuestions(ordered, false));
          setPendingDraft(null);
          setRetakeActive(true);
          return;
        }
        clearQuizInProgressSession(slug, documentId);
        setPendingDraft(null);
      }

      const ordered = randomize ? shuffleQuestions(allQuestions) : [...allQuestions];
      const orders = buildOptionOrdersForQuestions(ordered, shuffleOptions);
      clearQuizInProgressSession(slug, documentId);
      setAttemptQuestions(ordered);
      setAttemptSession(null);
      setOptionOrders(orders);
      setPendingDraft(null);
      setRetakeActive(true);
      setAttemptKey((k) => k + 1);
    },
    [
      slug,
      documentId,
      allQuestions,
      pendingDraft,
      randomize,
      shuffleOptions,
    ]
  );

  const handleStartOver = useCallback(() => {
    clearQuizInProgressSession(slug, documentId);
    setAttemptQuestions(null);
    setAttemptSession(null);
    setOptionOrders({});
    setPendingDraft(null);
    setDraftChecked(true);
    setRetakeActive(true);
    // Fresh attempt immediately after start-over from mid-quiz
    const ordered = randomize ? shuffleQuestions(allQuestions) : [...allQuestions];
    const orders = buildOptionOrdersForQuestions(ordered, shuffleOptions);
    setAttemptQuestions(ordered);
    setOptionOrders(orders);
    setAttemptKey((k) => k + 1);
  }, [slug, documentId, allQuestions, randomize, shuffleOptions]);

  const handlePlayAgain = useCallback(() => {
    clearQuizInProgressSession(slug, documentId);
    setAttemptQuestions(null);
    setAttemptSession(null);
    setOptionOrders({});
    setPendingDraft(null);
    setDraftChecked(true);
    setRetakeActive(true);
    setActiveSelection(null);
    setSessionTimeLimit(null);
    setTab('quiz');
    // Show preflight - user clicks Start for a new timed attempt
  }, [slug, documentId, setTab]);

  const clearExamSessionState = useCallback(() => {
    clearQuizInProgressSession(slug, documentId);
    setAttemptQuestions(null);
    setAttemptSession(null);
    setOptionOrders({});
    setPendingDraft(null);
    setDraftChecked(true);
    setRetakeActive(true);
    setActiveSelection(null);
    setSessionTimeLimit(null);
    setExitConfirm(null);
  }, [slug, documentId]);

  const clearPracticeSessionState = useCallback(() => {
    setPracticeDeck(null);
    setPracticeSelection(null);
    setExitConfirm(null);
  }, []);

  const requestExitExam = useCallback(() => {
    setExitConfirm('exam');
  }, []);

  const requestExitPractice = useCallback((answeredCount: number) => {
    if (answeredCount >= 3) {
      setExitConfirm('practice');
      return;
    }
    clearPracticeSessionState();
  }, [clearPracticeSessionState]);

  const startFixedPractice = useCallback(() => {
    const ordered = randomize ? shuffleQuestions(allQuestions) : [...allQuestions];
    setPracticeDeck(ordered);
    setPracticeSelection({
      question_ids: ordered.map((q) => q.id),
      count: ordered.length,
      mode: 'practice',
    });
  }, [allQuestions, randomize]);

  const startBankSession = useCallback(
    async (config: SessionBuilderConfig) => {
      if (!slug) return;
      setSessionStarting(true);
      try {
        const sampled = await sampleQuizQuestions(slug, {
          count: config.count,
          domains: config.domains.length > 0 ? config.domains : undefined,
          verified_only: config.verifiedOnly,
          exclude_mock_slug: config.excludeMock ? excludeMockSlug : undefined,
          domain_weights:
            config.mix === 'weighted' ? quizConfig.domain_weights : undefined,
        });
        const ordered = randomize
          ? shuffleQuestions(sampled.questions)
          : [...sampled.questions];
        const orders = buildOptionOrdersForQuestions(ordered, shuffleOptions);
        const selection = sampled.selection;

        if (config.mode === 'practice') {
          setPracticeDeck(ordered);
          setPracticeSelection(selection);
          setTab('practice');
          toast.message(`Practice deck: ${ordered.length} questions`);
          return;
        }

        clearQuizInProgressSession(slug, documentId);
        setActiveSelection(selection);
        setSessionTimeLimit(Math.max(1, ordered.length));
        setAttemptQuestions(ordered);
        setAttemptSession(null);
        setOptionOrders(orders);
        setPendingDraft(null);
        setRetakeActive(true);
        setAttemptKey((k) => k + 1);
        setTab('quiz');
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to start session');
      } finally {
        setSessionStarting(false);
      }
    },
    [
      slug,
      documentId,
      excludeMockSlug,
      quizConfig.domain_weights,
      randomize,
      shuffleOptions,
      setTab,
    ]
  );

  // Survey mode has no Practice tab - bounce if URL/tab stuck on practice
  useEffect(() => {
    if (isSurvey && tab === 'practice') setTab('quiz');
  }, [isSurvey, tab, setTab]);

  const handleSubmit = useCallback(
    async (displayAnswers: Record<string, string>, startedAt: Date) => {
      if (!slug) {
        toast.error('Quiz slug missing in frontmatter');
        return;
      }
      setSubmitting(true);
      try {
        const answers = toCanonicalAnswers(displayAnswers, optionOrders);
        const result = await submitQuizRun(slug, {
          answers,
          started_at: startedAt.toISOString(),
          completed_at: new Date().toISOString(),
          selection: activeSelection,
        });
        const mode = result.mode === 'survey' || isSurvey ? 'survey' : 'exam';
        setLastResult({
          run_id: result.run_id,
          mode,
          score_pct: result.score_pct,
          domain_scores: result.domain_scores,
          weak_domains: result.weak_domains,
          passed: result.passed,
          time_taken_s: result.time_taken_s,
          answered_count: Object.keys(answers).length,
        });
        setReviewDetail(null);
        clearQuizInProgressSession(slug, documentId);
        setAttemptSession(null);
        setAttemptQuestions(null);
        setOptionOrders({});
        setActiveSelection(null);
        setRetakeActive(false);
        setTab('results');
        refetchRuns();
        toast.success(
          mode === 'survey'
            ? 'Survey submitted'
            : `Quiz submitted: ${result.score_pct}%`,
        );
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Submit failed');
      } finally {
        setSubmitting(false);
      }
    },
    [slug, documentId, refetchRuns, setTab, isSurvey, optionOrders, activeSelection]
  );

  const openRunReview = useCallback(
    async (runId: string) => {
      if (!slug) return;
      setReviewLoading(true);
      try {
        const detail = await fetchQuizRunDetail(slug, runId);
        setReviewDetail(detail);
        setLastResult({
          run_id: detail.run_id,
          score_pct: detail.score_pct,
          domain_scores: detail.domain_scores,
          weak_domains: detail.weak_domains,
          passed: detail.passed,
          time_taken_s: detail.time_taken_s,
        });
        setTab('results');
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to load run review');
      } finally {
        setReviewLoading(false);
      }
    },
    [slug, setTab]
  );

  const handleGenerateDrill = useCallback(() => {
    const weak = (lastResult?.weak_domains || reviewDetail?.weak_domains || []).join(', ');
    const path = document.filePath || document.file_path || document.id;
    // Do not embed the bare slug in prose — the doc chip is the source of truth.
    // (A composer chip race used to splice the chip into the middle of "quiz-…".)
    const weakLabel = weak || 'none flagged';
    const query = [
      `Generate a targeted drill quiz for weak domains: ${weakLabel}.`,
      'The attached document is the source quiz bank.',
      'Use createQuizDocument with weakDomains set to those domains.',
    ].join(' ');
    window.dispatchEvent(
      new CustomEvent('flownotes:open-chat-prompt', {
        detail: {
          query,
          newThread: true,
          documentReference: {
            id: document.id,
            title: document.title,
            path,
            type: 'note',
          },
        },
      })
    );
  }, [document, lastResult, reviewDetail]);

  const showReview = !!reviewDetail && tab === 'results';

  return (
    <>
    <div
      className="h-full flex flex-col min-h-0 overflow-hidden bg-background"
      data-testid={testIds.quizDocumentRenderer.root}
    >
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-4">
        <GraduationCap className="h-4 w-4 shrink-0 text-teal-600" />
        <Badge variant="outline" className="shrink-0 text-xs">
          {allQuestions.length} questions
        </Badge>
        {isBank ? (
          <Badge variant="secondary" className="shrink-0 text-xs">
            bank
          </Badge>
        ) : null}
        <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          Slug: {slug || '-'}
          {isBank ? ' - Question bank' : ''}
          {isSurvey ? ' - Survey' : ''}
          {!isBank && timeLimitMinutes > 0 ? ` - ${timeLimitMinutes} min` : ''}
          {!isBank && !isSurvey ? ` - pass ${passingScore}%` : ''}
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as QuizTab)}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="mx-4 mt-3 flex items-center gap-2 shrink-0">
          <TabsList className="w-auto justify-start">
            <TabsTrigger value="quiz" data-testid={testIds.quizDocumentRenderer.quizTab}>
              {isSurvey ? 'Play' : 'Quiz'}
            </TabsTrigger>
            {!isSurvey ? (
              <TabsTrigger
                value="practice"
                data-testid={testIds.quizDocumentRenderer.practiceTab}
              >
                Practice
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="results">{isSurvey ? 'Summary' : 'Results'}</TabsTrigger>
            <TabsTrigger value="runs">Runs</TabsTrigger>
          </TabsList>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground"
                aria-label="Quiz setup and review"
                data-testid={testIds.quizDocumentRenderer.moreMenu}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}>
              <DropdownMenuItem
                className="gap-2 text-xs"
                data-testid={testIds.quizDocumentRenderer.reviewQuestionsMenuItem}
                onClick={() => setTab('preview')}
              >
                <Eye className="h-3.5 w-3.5 flex-none" />
                Review questions (show answers)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <TabsContent value="preview" className="mt-0 flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="mx-4 mt-3 mb-1 flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-950 dark:text-amber-100 shrink-0">
            <span>Review mode - answers and explanations are visible (setup / admin).</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs shrink-0"
              onClick={() => setTab('quiz')}
            >
              Back to quiz
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <QuizPreviewMode questions={allQuestions} />
          </div>
        </TabsContent>

        {!isSurvey ? (
          <TabsContent
            value="practice"
            className="mt-0 flex-1 min-h-0 overflow-hidden flex flex-col"
          >
            {isBank && !practiceDeck ? (
              <QuizSessionBuilder
                questions={allQuestions}
                defaultCount={defaultSessionCount}
                domainWeights={quizConfig.domain_weights}
                onStart={(cfg) => startBankSession({ ...cfg, mode: 'practice' })}
                starting={sessionStarting}
              />
            ) : !practiceDeck ? (
              <div
                className="flex-1 flex flex-col items-start justify-center gap-4 p-6"
                data-testid={testIds.quizDocumentRenderer.startGate}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-teal-500/15 text-teal-600">
                    <Play className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <h3 className="text-lg font-semibold">Start practice</h3>
                    <p className="text-sm text-muted-foreground">
                      {allQuestions.length} questions - no timer - immediate feedback
                      {shuffleOptions ? ' - options shuffled' : ''}
                    </p>
                  </div>
                </div>
                <Button
                  data-testid={testIds.quizDocumentRenderer.startQuiz}
                  onClick={startFixedPractice}
                  disabled={allQuestions.length === 0}
                >
                  Start practice
                </Button>
              </div>
            ) : (
              <QuizPracticeMode
                key={`practice:${slug}:${documentId}:${practiceDeck.length}:${practiceSelection?.question_ids?.[0] || 'x'}`}
                questions={practiceDeck}
                slug={slug}
                shuffleOptions={shuffleOptions}
                selection={practiceSelection}
                onExitSession={requestExitPractice}
                onDone={() => {
                  clearPracticeSessionState();
                  setTab('quiz');
                }}
              />
            )}
          </TabsContent>
        ) : null}

        <TabsContent
          value="quiz"
          forceMount
          className={cn(
            'mt-0 flex-1 min-h-0 overflow-hidden flex-col',
            tab === 'quiz' ? 'flex' : 'hidden'
          )}
        >
          {playLocked && lastResult ? (
            <div
              className="flex-1 flex flex-col items-start justify-center gap-4 p-6"
              data-testid={testIds.quizDocumentRenderer.completedPlay}
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-teal-500/15 text-teal-600">
                  {isSurvey || lastResult.mode === 'survey' ? (
                    <ListChecks className="h-5 w-5" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold">
                    {isSurvey || lastResult.mode === 'survey'
                      ? 'Survey already submitted'
                      : 'Quiz already submitted'}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Answers are locked for this run. Open Summary to review, or start a new
                    attempt.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid={testIds.quizDocumentRenderer.viewSummary}
                  onClick={() => setTab('results')}
                >
                  View summary
                </Button>
                <Button
                  variant="outline"
                  data-testid={testIds.quizDocumentRenderer.playAgain}
                  onClick={handlePlayAgain}
                >
                  Play again
                </Button>
              </div>
            </div>
          ) : attemptQuestions ? (
            <QuizActiveMode
              key={`${slug}:${documentId}:${attemptKey}`}
              questions={attemptQuestions}
              timeLimitMinutes={sessionTimeLimit ?? timeLimitMinutes}
              slug={slug}
              documentId={documentId}
              initialSession={attemptSession}
              optionOrders={optionOrders}
              onSubmit={handleSubmit}
              submitting={submitting}
              onStartOver={handleStartOver}
              onExitSession={requestExitExam}
              selection={activeSelection}
              submitLabel={isSurvey ? 'Finish' : 'Submit quiz'}
            />
          ) : !draftChecked || runsLoading ? (
            <p className="text-sm text-muted-foreground p-6">Loading quiz...</p>
          ) : allQuestions.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">
              No questions found in quiz fence.
            </p>
          ) : isBank && !pendingDraft ? (
            <QuizSessionBuilder
              questions={allQuestions}
              defaultCount={defaultSessionCount}
              domainWeights={quizConfig.domain_weights}
              onStart={startBankSession}
              starting={sessionStarting}
            />
          ) : (
            <div
              className="flex-1 flex flex-col items-start justify-center gap-4 p-6"
              data-testid={testIds.quizDocumentRenderer.startGate}
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-teal-500/15 text-teal-600">
                  <Play className="h-5 w-5" />
                </div>
                <div className="min-w-0 space-y-1">
                  <h3 className="text-lg font-semibold">
                    {pendingDraft ? 'Resume quiz?' : isSurvey ? 'Ready to start' : 'Start quiz'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {allQuestions.length} questions
                    {timeLimitMinutes > 0 ? ` - ${timeLimitMinutes} min timer` : ' - no timer'}
                    {!isSurvey ? ` - pass ${passingScore}%` : ''}
                    {shuffleOptions ? ' - options shuffled' : ''}
                  </p>
                  {pendingDraft ? (
                    <p className="text-xs text-muted-foreground">
                      In-progress attempt saved (
                      {Object.keys(pendingDraft.answers || {}).length} answered). Timer continues
                      from where you left off.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      The timer starts when you click Start. First question appears after Start.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {pendingDraft ? (
                  <>
                    <Button
                      data-testid={testIds.quizDocumentRenderer.resumeQuiz}
                      onClick={() => beginAttempt({ resume: true })}
                    >
                      Resume
                    </Button>
                    <Button
                      variant="outline"
                      data-testid={testIds.quizDocumentRenderer.startQuiz}
                      onClick={() => beginAttempt()}
                    >
                      Start over
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid={testIds.quizDocumentRenderer.startQuiz}
                    onClick={() => beginAttempt()}
                  >
                    {isSurvey ? 'Start survey' : 'Start quiz'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="results" className="mt-0 flex-1 min-h-0 overflow-hidden">
          {reviewLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading run review...
            </div>
          ) : showReview && reviewDetail && !isSurvey ? (
            <QuizRunReviewMode
              detail={reviewDetail}
              onBack={() => {
                setReviewDetail(null);
                setTab('runs');
              }}
              onGenerateDrill={
                (reviewDetail.weak_domains?.length || 0) > 0 ? handleGenerateDrill : undefined
              }
            />
          ) : lastResult ? (
            <QuizResultsMode
              mode={lastResult.mode ?? quizMode}
              scorePct={lastResult.score_pct}
              passingScore={passingScore}
              passed={lastResult.passed}
              domainScores={lastResult.domain_scores}
              weakDomains={lastResult.weak_domains}
              timeTakenS={lastResult.time_taken_s}
              answeredCount={lastResult.answered_count}
              onGenerateDrill={
                !isSurvey && lastResult.weak_domains.length > 0
                  ? handleGenerateDrill
                  : undefined
              }
              onReviewAnswers={
                !isSurvey && lastResult.run_id
                  ? () => {
                      void openRunReview(lastResult.run_id!);
                    }
                  : undefined
              }
            />
          ) : (
            <p className="text-sm text-muted-foreground p-6">
              {isSurvey
                ? 'Finish a survey to see a summary here.'
                : 'Complete a quiz run to see results here.'}
            </p>
          )}
        </TabsContent>

        <TabsContent value="runs" className="mt-0 flex-1 min-h-0 overflow-hidden">
          <QuizRunsTab
            runs={runs}
            loading={runsLoading}
            bestScore={bestScore}
            trend={trend}
            passingScore={passingScore}
            onSelectRun={(runId) => {
              void openRunReview(runId);
            }}
          />
        </TabsContent>
      </Tabs>
    </div>

    <AlertDialog
      open={exitConfirm === 'exam'}
      onOpenChange={(open) => {
        if (!open) setExitConfirm(null);
      }}
    >
      <AlertDialogContent data-testid={testIds.quizDocumentRenderer.exitExamConfirm}>
        <AlertDialogHeader>
          <AlertDialogTitle>Abandon exam?</AlertDialogTitle>
          <AlertDialogDescription>
            Your progress will not be saved. You will return to the session setup screen.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep going</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={clearExamSessionState}>
            Abandon exam
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog
      open={exitConfirm === 'practice'}
      onOpenChange={(open) => {
        if (!open) setExitConfirm(null);
      }}
    >
      <AlertDialogContent data-testid={testIds.quizDocumentRenderer.exitPracticeConfirm}>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave practice?</AlertDialogTitle>
          <AlertDialogDescription>
            Progress for this run will be cleared. You will return to the session setup screen.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep going</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={clearPracticeSessionState}>
            Exit session
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
